// /assets/:assetId/maintenance-items and /maintenance-items — the
// maintenance-item catalog and its per-store service history (ADR-0083).
// Sibling to checklist-templates.ts, never acta-bound: a maintenance item
// has no bloques/items, no acta, no folio, no signature — only a label, a
// fixed cadence, an applicability scope, and an append-only log of service
// dates per store.
//
// Only one freshness source exists here (the manual "marcar mantención"
// mark) — there is no acta-derived `lastReviewedAt` to resolve against,
// because Half A deliberately never binds a maintenance item to an acta
// (ADR-0083 Decision 6). That's why this file is simpler than
// checklist-templates.ts's `lastServicedSource` discriminator: with one
// source there is nothing to disambiguate. If Half B (acta-linked
// attestations) is ever built, a `source` field lands here additively —
// not before, per YAGNI.
import { assetIdSchema, maintenanceItemIdSchema, maintenanceItemSchema, } from "@bitacorapro/core-engine";
import { isoDateTimeSchema } from "@bitacorapro/shared";
import { z } from "zod";
// --- Per-store read: the "Mantenciones programadas" panel source -------
export const assetMaintenanceItemsParams = z.object({ assetId: assetIdSchema });
// Derived from the engine schema (invariant 7), matching
// checklistTemplateSummarySchema's re-derivation (ADR-0081 Decision 5) —
// id/label/cadenceDays are never hand-rolled a second time here.
export const maintenanceItemSummarySchema = maintenanceItemSchema
    .pick({ id: true, label: true, cadenceDays: true })
    .extend({
    /** Most recent service date recorded for this (asset, item) pair.
     *  Null when never serviced — the dashboard renders a distinct
     *  "sin registro" state for that case rather than fabricating
     *  freshness (see the fixed deriveMaintUrgency bug this entity's
     *  design avoids repeating, ADR-0083 Consequences). */
    lastServicedAt: isoDateTimeSchema.nullable(),
});
export const assetMaintenanceItemsResponse = z.object({
    items: z.array(maintenanceItemSummarySchema),
});
// --- Mark serviced: POST /assets/:assetId/maintenance-items/:itemId/events
export const markMaintenanceItemServicedParams = z.object({
    assetId: assetIdSchema,
    itemId: maintenanceItemIdSchema,
});
// `servicedAt` is bounded to "not in the future" (small clock-skew
// allowance) — the fix for the defect security-engineer found on
// checklist_template_reviews.reviewedAt (unbounded, silently parks an
// item as fresh indefinitely). Built correctly here from the start rather
// than repeating it; the checklist-template fix is a separate follow-up.
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1000;
export const markMaintenanceItemServicedBody = z.object({
    /** Defaults server-side to now() when omitted. An explicit past date
     *  covers "se revisó hace unos días, recién lo estoy registrando" —
     *  same rationale as markTemplateReviewedBody.reviewedAt. */
    servicedAt: isoDateTimeSchema
        .refine((value) => Date.parse(value) <= Date.now() + MAX_CLOCK_SKEW_MS, {
        message: "servicedAt cannot be in the future",
    })
        .optional(),
    /** Free text describing the work performed on the item — not who
     *  performed it (that's `servicedByUserId`), not why someone else
     *  didn't, and never a third party's information. This field became
     *  readable for the first time with the history endpoint (ADR-0086);
     *  before that it was write-only. It is now peer-visible to every
     *  asset-scoped role who can reach this store, not just org-scoped
     *  roles — the dashboard's write-time helper text must disclose that
     *  audience per ADR-0086's compliance note. */
    note: z
        .string()
        .max(500)
        .transform((v) => v.replace(/[\r\n]+/g, " ").trim())
        .optional(),
});
export const markMaintenanceItemServicedResponse = z.object({
    assetId: assetIdSchema,
    itemId: maintenanceItemIdSchema,
    lastServicedAt: isoDateTimeSchema,
    servicedBy: z.object({ id: z.string().uuid() }),
});
// --- Per-item service history: GET /assets/:assetId/maintenance-items/
//     :itemId/events (ADR-0086) ------------------------------------------
//
// Same params shape as the mark-serviced write — one params schema for
// one path serving both verbs, so the two never independently drift.
export const assetMaintenanceItemParams = markMaintenanceItemServicedParams;
export const maintenanceItemEventHistoryQuery = z.object({
    limit: z.coerce.number().int().positive().max(50).default(20),
});
// Actor stays an id, never a name — ADR-0085 Part 2 Option C's shipped
// precedent (markMaintenanceItemServicedResponse.servicedBy above), and
// ADR-0086's compliance note: denormalizing a display name here would
// turn a Ley 21.719 erasure request into a cross-row scan instead of one
// auth_users UPDATE. The dashboard resolves id -> displayName against
// its cached GET /auth/memberships roster.
const maintenanceEventActorSchema = z.object({ id: z.string().uuid() });
export const maintenanceItemEventSchema = z.object({
    id: z.string().uuid(),
    servicedAt: isoDateTimeSchema,
    /** The audit-insert timestamp — distinct from servicedAt on purpose
     *  (schema.ts). This is the only surface where that distinction
     *  reaches a human: it's what makes a backdated mark ("se revisó hace
     *  unos días, recién lo estoy registrando") visible as backdated. */
    recordedAt: isoDateTimeSchema,
    note: z.string().nullable(),
    servicedBy: maintenanceEventActorSchema,
    voidedAt: isoDateTimeSchema.nullable(),
    voidedBy: maintenanceEventActorSchema.nullable(),
    voidReason: z.string().nullable(),
});
// Unpaginated wire contract (ADR-0086): a per-(asset, item) history is
// physically bounded (a few rows/year at typical cadences), so a cursor
// is machinery for a problem that doesn't exist yet. `truncated` makes
// the one real edge case (an idempotency gap producing duplicate marks,
// or a pathological cadence) visible rather than silently dropping rows
// off a compliance record — silent truncation of an audit log is a
// correctness defect, not a UX one.
export const maintenanceItemEventHistoryResponse = z.object({
    events: z.array(maintenanceItemEventSchema),
    truncated: z.boolean(),
});
// --- Void a mark: POST /assets/:assetId/maintenance-items/:itemId/
//     events/:eventId/void (ADR-0086) -------------------------------------
//
// The compensating control for the widened write. ORG_SCOPED_ROLES only
// — never the same role set that can mark, or the void path solves
// nothing. Appends voidedAt/voidedByUserId/voidReason; never deletes or
// mutates servicedAt/servicedByUserId. A voided row stays in the table
// and drops out of the lastServicedAt freshness read.
export const voidMaintenanceItemEventParams = z.object({
    assetId: assetIdSchema,
    itemId: maintenanceItemIdSchema,
    eventId: z.string().uuid(),
});
export const voidMaintenanceItemEventBody = z.object({
    reason: z
        .string()
        .max(500)
        .transform((v) => v.replace(/[\r\n]+/g, " ").trim())
        .optional(),
});
export const voidMaintenanceItemEventResponse = z.object({
    id: z.string().uuid(),
    voidedAt: isoDateTimeSchema,
    voidedBy: maintenanceEventActorSchema,
});
// --- Admin configuration surface (mirrors checklist-templates.ts's) ----
// GET/POST/PATCH /maintenance-items, GET/PUT /maintenance-items/:id/assets.
// Writes gated to ORG_SCOPED_ROLES only — same self-audit-scope-escalation
// reasoning as checklist templates (ADR-0081 Decision 7), applied with the
// same force here since a service mark has no evidence behind it either.
export const maintenanceItemAdminSummary = maintenanceItemSchema
    .omit({ organizationId: true })
    .extend({
    // Denormalized read-time count, not a stored column — same pattern as
    // checklistTemplateAdminSummary.assignedAssetCount.
    assignedAssetCount: z.number().int().nonnegative(),
});
export const maintenanceItemAdminListResponse = z.object({
    items: z.array(maintenanceItemAdminSummary),
});
export const maintenanceItemParams = z.object({ itemId: maintenanceItemIdSchema });
// POST body — cadenceDays is required (not optional), unlike checklist
// templates: a maintenance item's whole reason to exist is a fixed cycle.
export const maintenanceItemCreateBody = maintenanceItemSchema
    .omit({
    id: true,
    organizationId: true,
    displayOrder: true,
    active: true,
    createdAt: true,
    updatedAt: true,
})
    .extend({
    displayOrder: z.number().int().optional(),
    assetIds: z.array(assetIdSchema).optional(),
});
// PATCH body — every field optional, at least one required. Same
// scope-flip guard as checklistTemplateUpdateBody: switching to
// scope:"assets" with zero resulting assignments would blank the item
// from every store's panel in one request.
export const maintenanceItemUpdateBody = maintenanceItemSchema
    .omit({ id: true, organizationId: true, brandId: true, createdAt: true, updatedAt: true })
    .partial()
    .extend({ assetIds: z.array(assetIdSchema).optional() })
    .refine((body) => Object.keys(body).length > 0, {
    message: "at least one field must be provided",
});
const maintenanceItemAssignedAssetSchema = z.object({
    id: assetIdSchema,
    unit: z.string().nullable(),
    address: z.string().nullable(),
});
export const maintenanceItemAssetsBody = z.object({
    assetIds: z.array(assetIdSchema),
});
export const maintenanceItemAssetsResponse = z.object({
    assets: z.array(maintenanceItemAssignedAssetSchema),
});
//# sourceMappingURL=maintenance.js.map